<template>
  <div
    ref="container"
    class="image-zoom-container"
    :style="containerStyle"
    @wheel.prevent="handleWheel"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <v-img
      ref="image"
      :src="src"
      :alt="alt"
      :width="imageWidth"
      :height="imageHeight"
      :contain="fit === 'contain'"
      :aspect-ratio="aspectRatio"
      :eager="eager"
      :lazy-src="lazySrc"
      class="zoom-image"
      :style="imageStyle"
      @load="handleImageLoad"
      @error="handleImageError"
    >
      <template v-slot:placeholder v-if="$slots.placeholder">
        <slot name="placeholder"></slot>
      </template>
    </v-img>
  </div>
</template>

<script>
export default {
  name: "ImageZoom",
  props: {
    src: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: "",
    },
    width: {
      type: [Number, String],
      default: "100%",
    },
    height: {
      type: [Number, String],
      default: "auto",
    },
    minZoom: {
      type: Number,
      default: 1,
    },
    maxZoom: {
      type: Number,
      default: 4,
    },
    zoomStep: {
      type: Number,
      default: 0.1,
    },
    resetOnDoubleClick: {
      type: Boolean,
      default: true,
    },
    panEnabled: {
      type: Boolean,
      default: true,
    },
    fit: {
      type: String,
      default: "contain",
      validator: (value) => ["contain", "cover"].includes(value),
    },
    aspectRatio: {
      type: [Number, String],
      default: undefined,
    },
    eager: {
      type: Boolean,
      default: false,
    },
    lazySrc: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      lastPanPoint: { x: 0, y: 0 },
      touches: [],
      lastTouchDistance: 0,
      lastTouchCenter: { x: 0, y: 0 },
      imageLoaded: false,
      initialScale: 1,
      containerWidth: 0,
      containerHeight: 0,
      naturalWidth: 0,
      naturalHeight: 0,
    };
  },
  computed: {
    containerStyle() {
      return {
        width: typeof this.width === "number" ? `${this.width}px` : this.width,
        height:
          typeof this.height === "number" ? `${this.height}px` : this.height,
        overflow: "hidden",
        position: "relative",
        cursor: this.isDragging ? "grabbing" : "grab",
        touchAction: "none",
      };
    },
    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transformOrigin: "0 0",
        transition: this.isDragging ? "none" : "transform 0.1s ease-out",
      };
    },
    imageWidth() {
      // Para v-img, retornamos as dimensões do container
      return typeof this.width === "number" ? this.width : "100%";
    },
    imageHeight() {
      return typeof this.height === "number" ? this.height : "100%";
    },
  },
  mounted() {
    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);

    if (this.resetOnDoubleClick) {
      this.$refs.container.addEventListener("dblclick", this.resetZoom);
    }

    // Observar mudanças no tamanho do container
    this.resizeObserver = new ResizeObserver(() => {
      if (this.imageLoaded) {
        this.updateContainerDimensions();
        this.fitImageToContainer();
      }
    });
    this.resizeObserver.observe(this.$refs.container);

    this.$nextTick(() => {
      this.updateContainerDimensions();
    });
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);

    if (this.resetOnDoubleClick) {
      this.$refs.container.removeEventListener("dblclick", this.resetZoom);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    updateContainerDimensions() {
      const container = this.$refs.container;
      if (container) {
        this.containerWidth = container.clientWidth;
        this.containerHeight = container.clientHeight;
      }
    },

    handleImageLoad(event) {
      this.imageLoaded = true;

      // Para v-img, precisamos acessar o elemento img interno
      const imgElement = this.$refs.image.$el.querySelector("img");
      if (imgElement) {
        this.naturalWidth = imgElement.naturalWidth;
        this.naturalHeight = imgElement.naturalHeight;
      }

      this.updateContainerDimensions();
      this.$emit("image-loaded", event);

      this.$nextTick(() => {
        this.fitImageToContainer();
      });
    },

    handleImageError(event) {
      this.$emit("image-error", event);
    },

    fitImageToContainer() {
      if (
        !this.imageLoaded ||
        !this.containerWidth ||
        !this.containerHeight ||
        !this.naturalWidth ||
        !this.naturalHeight
      ) {
        return;
      }

      const scaleX = this.containerWidth / this.naturalWidth;
      const scaleY = this.containerHeight / this.naturalHeight;

      let fitScale = 1;

      if (this.fit === "contain") {
        // v-img com contain já faz isso, então nossa escala inicial é 1
        // Mas precisamos calcular qual seria a escala real para referência
        fitScale = Math.min(scaleX, scaleY);
      } else if (this.fit === "cover") {
        fitScale = Math.max(scaleX, scaleY);
      }

      // Para v-img, a escala inicial é sempre 1 (o v-img já ajusta internamente)
      this.initialScale = 1;
      this.scale = Math.max(1, this.minZoom);
      this.centerImage();
    },

    centerImage() {
      if (!this.imageLoaded || !this.containerWidth || !this.containerHeight)
        return;

      // Para v-img, centralizamos baseado no tamanho atual scaled
      const scaledWidth = this.containerWidth * this.scale;
      const scaledHeight = this.containerHeight * this.scale;

      this.translateX = (this.containerWidth - scaledWidth) / 2;
      this.translateY = (this.containerHeight - scaledHeight) / 2;
    },

    handleWheel(event) {
      if (!this.imageLoaded) return;

      const delta = -event.deltaY;
      const zoomFactor = delta > 0 ? 1 + this.zoomStep : 1 - this.zoomStep;

      const rect = this.$refs.container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      this.zoomAtPoint(x, y, zoomFactor);
    },

    zoomAtPoint(x, y, zoomFactor) {
      const newScale = Math.min(
        Math.max(this.scale * zoomFactor, this.minZoom),
        this.maxZoom
      );

      if (newScale === this.scale) return;

      const scaleRatio = newScale / this.scale;

      this.translateX = x - (x - this.translateX) * scaleRatio;
      this.translateY = y - (y - this.translateY) * scaleRatio;
      this.scale = newScale;

      this.constrainPan();
      this.$emit("zoom-change", {
        scale: this.scale,
        x: this.translateX,
        y: this.translateY,
        zoomLevel: this.scale / this.initialScale,
      });
    },

    handleMouseDown(event) {
      if (!this.panEnabled || !this.imageLoaded) return;

      this.isDragging = true;
      this.lastPanPoint = { x: event.clientX, y: event.clientY };
    },

    handleMouseMove(event) {
      if (!this.isDragging || !this.panEnabled) return;

      const deltaX = event.clientX - this.lastPanPoint.x;
      const deltaY = event.clientY - this.lastPanPoint.y;

      this.translateX += deltaX;
      this.translateY += deltaY;

      this.lastPanPoint = { x: event.clientX, y: event.clientY };
      this.constrainPan();
    },

    handleMouseUp() {
      this.isDragging = false;
    },

    handleTouchStart(event) {
      if (!this.imageLoaded) return;

      this.touches = Array.from(event.touches);

      if (this.touches.length === 1) {
        this.isDragging = this.panEnabled;
        this.lastPanPoint = {
          x: this.touches[0].clientX,
          y: this.touches[0].clientY,
        };
      } else if (this.touches.length === 2) {
        this.isDragging = false;
        const distance = this.getTouchDistance();
        const center = this.getTouchCenter();

        this.lastTouchDistance = distance;
        this.lastTouchCenter = center;
      }
    },

    handleTouchMove(event) {
      if (!this.imageLoaded) return;

      event.preventDefault();
      this.touches = Array.from(event.touches);

      if (this.touches.length === 1 && this.isDragging && this.panEnabled) {
        const deltaX = this.touches[0].clientX - this.lastPanPoint.x;
        const deltaY = this.touches[0].clientY - this.lastPanPoint.y;

        this.translateX += deltaX;
        this.translateY += deltaY;

        this.lastPanPoint = {
          x: this.touches[0].clientX,
          y: this.touches[0].clientY,
        };
        this.constrainPan();
      } else if (this.touches.length === 2) {
        const distance = this.getTouchDistance();
        const center = this.getTouchCenter();
        const rect = this.$refs.container.getBoundingClientRect();

        if (this.lastTouchDistance > 0) {
          const zoomFactor = distance / this.lastTouchDistance;
          const x = center.x - rect.left;
          const y = center.y - rect.top;

          this.zoomAtPoint(x, y, zoomFactor);
        }

        this.lastTouchDistance = distance;
        this.lastTouchCenter = center;
      }
    },

    handleTouchEnd(event) {
      this.touches = Array.from(event.touches);

      if (this.touches.length === 0) {
        this.isDragging = false;
        this.lastTouchDistance = 0;
      } else if (this.touches.length === 1) {
        this.isDragging = this.panEnabled;
        this.lastPanPoint = {
          x: this.touches[0].clientX,
          y: this.touches[0].clientY,
        };
        this.lastTouchDistance = 0;
      }
    },

    getTouchDistance() {
      if (this.touches.length < 2) return 0;

      const dx = this.touches[0].clientX - this.touches[1].clientX;
      const dy = this.touches[0].clientY - this.touches[1].clientY;

      return Math.sqrt(dx * dx + dy * dy);
    },

    getTouchCenter() {
      if (this.touches.length < 2) return { x: 0, y: 0 };

      return {
        x: (this.touches[0].clientX + this.touches[1].clientX) / 2,
        y: (this.touches[0].clientY + this.touches[1].clientY) / 2,
      };
    },

    constrainPan() {
      if (!this.imageLoaded || !this.containerWidth || !this.containerHeight)
        return;

      const scaledWidth = this.containerWidth * this.scale;
      const scaledHeight = this.containerHeight * this.scale;

      const maxTranslateX = 0;
      const minTranslateX = this.containerWidth - scaledWidth;
      const maxTranslateY = 0;
      const minTranslateY = this.containerHeight - scaledHeight;

      if (scaledWidth <= this.containerWidth) {
        this.translateX = (this.containerWidth - scaledWidth) / 2;
      } else {
        this.translateX = Math.min(
          Math.max(this.translateX, minTranslateX),
          maxTranslateX
        );
      }

      if (scaledHeight <= this.containerHeight) {
        this.translateY = (this.containerHeight - scaledHeight) / 2;
      } else {
        this.translateY = Math.min(
          Math.max(this.translateY, minTranslateY),
          maxTranslateY
        );
      }
    },

    resetZoom() {
      this.scale = this.initialScale;
      this.centerImage();
      this.$emit("zoom-reset", {
        scale: this.scale,
        initialScale: this.initialScale,
      });
    },

    zoomIn() {
      const centerX = this.containerWidth / 2;
      const centerY = this.containerHeight / 2;

      this.zoomAtPoint(centerX, centerY, 1 + this.zoomStep);
    },

    zoomOut() {
      const centerX = this.containerWidth / 2;
      const centerY = this.containerHeight / 2;

      this.zoomAtPoint(centerX, centerY, 1 - this.zoomStep);
    },

    // Métodos públicos
    getZoomInfo() {
      return {
        currentScale: this.scale,
        initialScale: this.initialScale,
        zoomLevel: this.scale / this.initialScale,
        translateX: this.translateX,
        translateY: this.translateY,
        fit: this.fit,
        containerWidth: this.containerWidth,
        containerHeight: this.containerHeight,
        naturalWidth: this.naturalWidth,
        naturalHeight: this.naturalHeight,
      };
    },
  },
};
</script>

<style scoped>
.image-zoom-container {
  background-color: #f5f5f5;
  border-radius: 4px;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.zoom-image {
  width: 100% !important;
  height: 100% !important;
}

.image-zoom-container:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Sobrescrever estilos do v-img se necessário */
.zoom-image >>> .v-image {
  pointer-events: none;
}

.zoom-image >>> .v-image__image {
  transition: inherit;
}
</style>
